import { memo, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import { buildAccommodationCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { getPropertyCancellationLabelShape, getPropertyRefundableStatus, getRefundDescriptions } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import useRestrictedPropertyCancellationPolicy from 'hooks/useRestrictedPropertyCancellationPolicy'
import PropertyCancellationPolicyView from './PropertyCancellationPolicyView'

interface Props {
  propertyTimeZone: string
  headlineCancellationPolicyType: 'fullyRefundable' | 'partiallyRefundable' | 'nonRefundable'
  headlineCancellationPolicyStart: Date | undefined
  fullyRefundableUntil: string | undefined
  cancellationPolicies: Array<App.Property.CancellationPolicy>
  currency: string
  shouldClarifyRefundIsHotelOnly: boolean
  restrictedPolicyEligible: boolean
}

function PropertyCancellationPolicy({
  propertyTimeZone,
  headlineCancellationPolicyType,
  headlineCancellationPolicyStart,
  fullyRefundableUntil,
  cancellationPolicies,
  currency,
  shouldClarifyRefundIsHotelOnly,
  restrictedPolicyEligible,
}: Props) {
  const intl = useIntl()
  const refundDescriptions = useMemo(() => getRefundDescriptions(undefined, intl), [intl])
  const refundableStatus = getPropertyRefundableStatus(headlineCancellationPolicyType)
  const refundDescription = refundDescriptions[refundableStatus]

  const { description: policyDescriptions } = useMemo(() => buildAccommodationCancellationPolicies({
    fullRefundableUntil: fullyRefundableUntil,
    cancellationPolicies: cancellationPolicies.map(({
      startIsoDateTime,
      endIsoDateTime,
      penaltyPrice,
      isFullyNonRefundable,
      originalDescription,
    }) => ({
      // TODO(HP-1333): Update `lib-refunds` to conform to Customer Portal interface.
      amount: penaltyPrice,
      percent: 0,
      description: originalDescription,
      start: startIsoDateTime,
      end: endIsoDateTime,
      nonRefundable: isFullyNonRefundable,
    })),
  }, {
    timezone: propertyTimeZone,
    currencyCode: currency,
    hideExcludesFlights: !shouldClarifyRefundIsHotelOnly,
    locale: intl.locale,
  }), [
    fullyRefundableUntil,
    cancellationPolicies,
    propertyTimeZone,
    currency,
    shouldClarifyRefundIsHotelOnly,
    intl.locale,
  ])

  const [activationRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
    skip: !restrictedPolicyEligible,
  })
  const restrictedView = useRestrictedPropertyCancellationPolicy(
    headlineCancellationPolicyStart,
    propertyTimeZone,
    restrictedPolicyEligible,
    policyDescriptions,
    { canActivate: inView },
  )

  const labelShape = useMemo(() => getPropertyCancellationLabelShape({
    refundable: refundableStatus,
    headlineCancellationPolicyStart,
    propertyTimezone: propertyTimeZone,
    shouldClarifyRefundIsHotelOnly,
  }), [refundableStatus, headlineCancellationPolicyStart, propertyTimeZone, shouldClarifyRefundIsHotelOnly])

  return <PropertyCancellationPolicyView
    refundableStatus={refundableStatus}
    refundDescription={refundDescription}
    labelShape={labelShape}
    policyDescriptions={policyDescriptions}
    restrictedView={restrictedView}
    activationRef={restrictedPolicyEligible ? activationRef : undefined}
  />
}

export default memo(PropertyCancellationPolicy)
